import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import AllHoldingsModal from "../AllHoldingsModal";
import { useInvestmentOrder } from "features/Explore/hook";
import {
  Card,
  CelebrationCard,
  TabsContainer,
  Tab,
  FormContent,
  FormGroupRow,
  AvailableCashRow,
  Divider,
  AlertBox,
  AlertIcon,
  AlertText,
  BackButton,
  BackIcon,
  SummaryTitle,
  SummarySection,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  Spinner,
  OrderSubmittedHeader,
  OrderSubmittedTitle,
  ViewDetailsButton,
  ControlContainer,
  StrategyLogo,
  StrategyInfo,
  ButtonsContainer,
  LockIcon,
  InputHelperRow,
  SellAllButton,
} from "./InvestmentOrderCard.styles";
import { Button } from "components/Button";
import { Dropdown } from "components/Dropdown/v2";
import { Text } from "style/v2/components/Typography";

import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import "bootstrap/dist/css/bootstrap.min.css";
import { isFreeUser } from "utils";
import { userDetailSelect } from "Layout/slice/selector";
import { ChoosePlanModal } from "features/Account/v2/ChoosePlanModal/index";
import confetti from "canvas-confetti";
import { BLOCKED_BROKERS } from "utils/constants";

const InvestmentOrderCard = ({
  isPublicStrategy = false,
  canInvest = true,
}) => {
  const { id: strategyId } = useParams();
  const [showPurchasePlanModal, setShowPurchasePlanModal] = useState(false);
  const togglePurchasePlanModal = useCallback(() => {
    setShowPurchasePlanModal((prev) => !prev);
  }, []);

  const cardRef = useRef(null);
  const user = useSelector(userDetailSelect);
  // Lock should only apply to plan restrictions (free). Users on Free plan without a confirmed internal account are locked.
  const isPlanLocked = useMemo(
    () => isFreeUser() && !user?.is_surmount_internal_created,
    [user?.is_surmount_internal_created]
  );
  const isPublicDisabled = Boolean(isPublicStrategy);

  const {
    // State
    activeTab,
    step,
    selectedAccount,
    investmentAmount,
    isSubmitting,
    orderSubmitted,
    strategyData,
    tradingConnectionData,
    isLoadingCash,
    isHoldingsModalOpen,
    showSellAll,

    // API Data
    portfolios,
    loading,
    isError,
    holdingsData,
    isLoadingHoldings,
    isHoldingsError,

    // Computed values
    availableCashFormatted,
    strategy,
    additionalCash,
    showAdditionalCashAlert,
    isAmountValid,

    // Setters
    setActiveTab,
    setInvestmentAmount,
    setIsHoldingsModalOpen,

    // Event handlers
    handleNext,
    handleBack,
    handleSubmitOrder,
    handleDone,
    handleViewDetails,
    handleAccountSelect,
    handleSellAll,
  } = useInvestmentOrder(strategyId);

  useEffect(() => {
    if (!orderSubmitted || !cardRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.top = 0;
    canvas.style.left = 0;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    cardRef.current.appendChild(canvas);

    const myConfetti = confetti.create(canvas, { resize: true });
    const count = 200;
    const defaults = { origin: { y: 0.7 } };

    function fire(particleRatio, opts) {
      myConfetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // Fire sequence
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });

    return () => {
      myConfetti.reset();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, [orderSubmitted]);

  // Sell label shows the account's invested snapshot to match the dropdown/dashboard.
  const allocationOrCashDisplay = useMemo(() => {
    if (activeTab === "sell") {
      return selectedAccount?.invested?.formatted || "Not available";
    }
    return availableCashFormatted;
  }, [activeTab, selectedAccount, availableCashFormatted]);

  const filteredPortfolios = useMemo(() => {
    if (!portfolios) return [];
    return portfolios.filter((account) => {
      const identifier = account.broker_key || account.broker_name || "";
      return !BLOCKED_BROKERS.includes(identifier.toUpperCase());
    });
  }, [portfolios]);

  const accountOptions = useMemo(() => {
    return filteredPortfolios.map((account) => {
      const isNotConnected =
        account.subscription_status?.toLowerCase()?.trim() !== "active";
      const isDisabled = activeTab === "sell" && isNotConnected;
      const label = account.broker_account_nickname || account.broker_name;
      const invested = account.invested?.formatted ?? "$0.00";
      return {
        value: account.broker_account_id,
        label,
        icon: account.logo_url,
        description: `Invested: ${invested}`,
        title: `${label} (Invested: ${invested})`,
        disabled: isDisabled,
      };
    });
  }, [filteredPortfolios, activeTab]);

  // Order Submitted View
  if (orderSubmitted) {
    return (
      <>
        <CelebrationCard ref={cardRef}>
          <OrderSubmittedHeader>
            <OrderSubmittedTitle>
              {activeTab === "sell"
                ? "Sell Order Submitted"
                : "Buy Order Submitted"}
            </OrderSubmittedTitle>
            <Text variant="body" color="tertiary" as="p">
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </Text>
          </OrderSubmittedHeader>

          <SummarySection>
            <SummaryRow>
              <SummaryLabel>Strategy</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {strategyData?.logo_url && (
                    <StrategyLogo
                      src={strategyData.logo_url}
                      alt={strategy}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>{strategy}</span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Account</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {selectedAccount?.logo_url && (
                    <StrategyLogo
                      src={selectedAccount.logo_url}
                      alt={selectedAccount?.broker_name || "Broker"}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>
                    {selectedAccount?.broker_name ||
                      selectedAccount?.broker_name ||
                      "Selected account"}
                  </span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Estimated cost</SummaryLabel>
              <SummaryValue $amount>${investmentAmount} USD</SummaryValue>
            </SummaryRow>
          </SummarySection>

          <ButtonsContainer>
            <Button
              variant="primary"
              size="large"
              className="w-100"
              onClick={handleDone}
            >
              Done
            </Button>
            <ViewDetailsButton onClick={handleViewDetails}>
              View details
            </ViewDetailsButton>
          </ButtonsContainer>
        </CelebrationCard>

        <AllHoldingsModal
          isOpen={isHoldingsModalOpen}
          onClose={() => {
            setIsHoldingsModalOpen(false);
          }}
          holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
          strategyName={strategy}
          isLoading={isLoadingHoldings}
          error={isHoldingsError}
        />
        {/* Purchase Plan Modal for locked interactions */}
        {/* <PurchasePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
          isCancelPlan={false}
          isChangePlan={true}
        /> */}
        <ChoosePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
        />
      </>
    );
  }

  // Summary View (Step 2)
  if (step === 2) {
    return (
      <>
        <Card>
          <BackButton onClick={handleBack}>
            <BackIcon>←</BackIcon> Back
          </BackButton>

          <SummaryTitle>Summary</SummaryTitle>

          <SummarySection>
            <SummaryRow>
              <SummaryLabel>Strategy</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {strategyData?.logo_url && (
                    <StrategyLogo
                      src={strategyData.logo_url}
                      alt={strategy}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>{strategy}</span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Account</SummaryLabel>
              <SummaryValue>
                {selectedAccount?.broker_name ||
                  selectedAccount?.broker_name ||
                  "Selected account"}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Investment amount</SummaryLabel>
              <SummaryValue $amount>${investmentAmount} USD</SummaryValue>
            </SummaryRow>
          </SummarySection>

          <Button
            variant="secondary"
            size="large"
            className="w-100"
            onClick={handleSubmitOrder}
            disabled={isSubmitting}
          >
            Submit order {isSubmitting && <Spinner />}
          </Button>
        </Card>

        <AllHoldingsModal
          isOpen={isHoldingsModalOpen}
          onClose={() => {
            setIsHoldingsModalOpen(false);
          }}
          holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
          strategyName={strategy}
          isLoading={isLoadingHoldings}
          error={isHoldingsError}
        />
        {/* Purchase Plan Modal for locked interactions */}
        {/* <PurchasePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
          isCancelPlan={false}
          isChangePlan={true}
        /> */}
        <ChoosePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
        />
      </>
    );
  }

  // Initial Form View (Step 1)
  return (
    <>
      <Card>
        <TabsContainer>
          <Tab
            $active={activeTab === "buy"}
            onClick={() => setActiveTab("buy")}
          >
            Buy
          </Tab>
          <Tab
            $active={activeTab === "sell"}
            onClick={() => setActiveTab("sell")}
          >
            Sell
          </Tab>
        </TabsContainer>

        <FormContent>
          {/* Account Selection */}
          <FormGroupRow $stacked $mb="1rem">
            <Text variant="bodyMedium" color="tertiary" as="label">
              Account
            </Text>
            <ControlContainer>
              <Dropdown
                block
                options={accountOptions}
                value={selectedAccount?.broker_account_id}
                onChange={handleAccountSelect}
                placeholder="Select an account"
                disabled={isPublicStrategy}
                emptyText="No accounts available"
                loadingText={loading ? "Loading accounts..." : undefined}
                errorText={
                  isError ? "Failed to load broker accounts" : undefined
                }
              />
            </ControlContainer>
          </FormGroupRow>

          {selectedAccount && (
            <AvailableCashRow>
              <Text variant="body" color="tertiary" as="span">
                {activeTab === "sell" ? "Current Allocation" : "Available cash"}
              </Text>
              <Text variant="cardHeading" weight="500" as="span">
                {isLoadingCash ? (
                  <Spinner />
                ) : tradingConnectionData ? (
                  allocationOrCashDisplay
                ) : (
                  "Unable to load"
                )}
              </Text>
            </AvailableCashRow>
          )}

          {/* Investment Amount */}
          <FormGroupRow $mt="1.5rem">
            <Text variant="bodyMedium" color="tertiary" as="label">
              Investment amount
            </Text>
            <ControlContainer>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  disabled={isPublicStrategy}
                />
                <InputGroup.Text>USD</InputGroup.Text>
              </InputGroup>
              {showSellAll && !isPublicStrategy && (
                <InputHelperRow>
                  <SellAllButton onClick={handleSellAll} type="button">
                    Sell All
                  </SellAllButton>
                </InputHelperRow>
              )}
            </ControlContainer>
          </FormGroupRow>
          {!isAmountValid && investmentAmount !== "" && (
            <AlertBox>
              <AlertIcon>!</AlertIcon>
              <AlertText>Enter a valid amount greater than 0.</AlertText>
            </AlertBox>
          )}

          {showAdditionalCashAlert && (
            <>
              <Divider />
              <AlertBox>
                <AlertText>
                  You will add <strong>${additionalCash} USD</strong> in the
                  next step to complete this order.
                </AlertText>
              </AlertBox>
            </>
          )}
          <Button
            variant="primary"
            size="large"
            className="w-100"
            title={
              !canInvest
                ? "This strategy is no longer available for new investments."
                : isPublicDisabled
                ? "Public strategies cannot be ordered from here."
                : isPlanLocked
                ? "Upgrade your plan to proceed to the next step."
                : "Proceed to the order summary."
            }
            onClick={
              !canInvest || isPublicDisabled
                ? undefined
                : isPlanLocked
                ? togglePurchasePlanModal
                : handleNext
            }
            disabled={
              !canInvest ||
              isPublicDisabled ||
              (!isPlanLocked && (!selectedAccount || !isAmountValid))
            }
          >
            {isPlanLocked && !isPublicDisabled && <LockIcon />}
            Next
          </Button>
        </FormContent>
      </Card>

      <AllHoldingsModal
        isOpen={isHoldingsModalOpen}
        onClose={() => {
          setIsHoldingsModalOpen(false);
        }}
        holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
        strategyName={strategy}
        isLoading={isLoadingHoldings}
        error={isHoldingsError}
      />
      {/* Purchase Plan Modal for locked interactions */}
      {/* <PurchasePlanModal
        show={showPurchasePlanModal}
        onHide={togglePurchasePlanModal}
        isCancelPlan={false}
        isChangePlan={true}
      /> */}
      <ChoosePlanModal
        show={showPurchasePlanModal}
        onHide={togglePurchasePlanModal}
      />
    </>
  );
};

InvestmentOrderCard.propTypes = {
  isPublicStrategy: PropTypes.bool,
  canInvest: PropTypes.bool,
};

export default InvestmentOrderCard;
